import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';

export const authHeaders = () => {
  const token = localStorage.getItem('Token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const normalizeRestaurantShortCode = (value) =>
  String(value || '').trim().toUpperCase();

export function isRestaurantDefault(restaurant) {
  if (!restaurant) return false;
  const def =
    restaurant.isDefault ?? restaurant.IsDefault ?? restaurant.is_default;
  return def === true || def === 1 || def === '1' || def === 'true';
}

/** Map API isDefault values to edit-form select values ("1" / "0"). */
export function normalizeIsDefaultFormValue(value) {
  if (value === undefined || value === null || value === '') return '';
  return isRestaurantDefault({ isDefault: value }) ? '1' : '0';
}

export function isRestaurantActive(restaurant) {
  if (!restaurant) return false;
  const status = pickValue(restaurant, 'status', 'Status');
  const normalized = String(status ?? '').trim().toUpperCase();
  return normalized === 'ACTIVE' || normalized === 'A';
}

const restaurantSummaryScore = (row) => {
  if (!row) return 0;
  let score = 0;
  if (extractRestaurantName(row)) score += 1;
  const status = pickValue(row, 'status', 'Status');
  if (status != null && String(status).trim() !== '') score += 4;
  if (pickValue(row, 'isDefault', 'IsDefault', 'is_default') !== undefined) score += 1;
  return score;
};

const hasRestaurantStatus = (row) => {
  const status = pickValue(row, 'status', 'Status');
  return status != null && String(status).trim() !== '';
};

/** Prefer the restaurant marked default; otherwise first list entry. */
export function pickDefaultRestaurantFromList(restaurants) {
  if (!Array.isArray(restaurants) || restaurants.length === 0) return null;
  return restaurants.find(isRestaurantDefault) ?? restaurants[0];
}

/** GET /restaurant/:userId/default — restaurant with Is_Default for this user. */
export async function fetchDefaultRestaurantForUser(userId) {
  if (!userId) return null;
  try {
    const response = await axiosRestaurantInstance.get(
      RESTAURANT_API_PATHS.restaurantUserDefault(userId),
      { headers: authHeaders() }
    );
    return normalizeRestaurantDetail(response.data);
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
}

/**
 * Resolve which restaurant to show:
 * - Refresh / restaurant switch: persisted localStorage selection (if still accessible)
 * - Sign-in: default restaurant (Signin clears localStorage before navigation)
 */
export async function resolveInitialRestaurant(userId, restaurantsList = null) {
  let list = Array.isArray(restaurantsList) ? restaurantsList : [];

  if (list.length === 0 && userId) {
    try {
      list = await fetchUserRestaurants(userId);
    } catch (error) {
      console.warn('Could not load user restaurants:', error);
    }
  }

  let restaurant = null;

  const storedId = localStorage.getItem('restaurantId');
  if (storedId && storedId !== 'null' && storedId !== 'undefined') {
    const storedInList =
      list.length === 0 ||
      list.some((r) => String(extractRestaurantId(r)) === String(storedId));
    if (storedInList) {
      try {
        restaurant = await fetchRestaurantById(storedId);
      } catch (error) {
        console.warn('Could not load stored restaurant:', error);
        const summary = list.find((r) => String(extractRestaurantId(r)) === String(storedId));
        if (summary) {
          restaurant = normalizeRestaurantDetail(summary);
        }
      }
    }
  }

  if (!restaurant && list.length > 0) {
    const picked = pickDefaultRestaurantFromList(list);
    const pickedId = extractRestaurantId(picked);
    if (pickedId) {
      try {
        restaurant = await fetchRestaurantById(pickedId);
      } catch (error) {
        console.warn('Could not load picked restaurant by id:', error);
        restaurant = normalizeRestaurantDetail(picked);
      }
    }
  }

  if (!restaurant && userId) {
    try {
      restaurant = await fetchDefaultRestaurantForUser(userId);
    } catch (error) {
      console.warn('Could not load default restaurant:', error);
    }
  }

  return restaurant;
}

export const RESTAURANT_MANAGER_ROLE = 'MANAGER';
export const RESTAURANT_DELIVERY_AGENT_ROLE = 'DELIVERY_AGENT';
export const RESTAURANT_CASHIER_ROLE = 'CASHIER';
export const RESTAURANT_SELF_CHECKOUT_ROLE = 'SELF_CHECKOUT';

/** Map API / legacy UI role strings to role_mt.Role_Name values. */
export function normalizeRestaurantRoleName(role) {
  const raw = String(role ?? '').trim();
  if (!raw) return '';

  const upper = raw.toUpperCase().replace(/[\s-]+/g, '_');
  if (
    upper === RESTAURANT_MANAGER_ROLE ||
    upper === RESTAURANT_DELIVERY_AGENT_ROLE ||
    upper === RESTAURANT_CASHIER_ROLE ||
    upper === RESTAURANT_SELF_CHECKOUT_ROLE
  ) {
    return upper;
  }

  const compact = raw.toLowerCase().replace(/[\s_-]+/g, '');
  if (compact === 'manager') return RESTAURANT_MANAGER_ROLE;
  if (compact === 'deliveryagent') return RESTAURANT_DELIVERY_AGENT_ROLE;
  if (compact === 'cashier') return RESTAURANT_CASHIER_ROLE;
  if (compact === 'selfcheckout') return RESTAURANT_SELF_CHECKOUT_ROLE;

  return raw;
}

export function normalizeRestaurantRoleNames(roles) {
  return [
    ...new Set(
      (Array.isArray(roles) ? roles : [])
        .map(normalizeRestaurantRoleName)
        .filter(Boolean)
    ),
  ];
}

export function mapRestaurantUserRow(u) {
  if (!u || typeof u !== 'object') return null;
  const rawRoles = Array.isArray(u.roles)
    ? u.roles
    : u.roleName
      ? [u.roleName]
      : u.role_name
        ? [u.role_name]
        : [];

  const entityRolesRaw = Array.isArray(u.entityRoles)
    ? u.entityRoles
    : Array.isArray(u.entity_roles)
      ? u.entity_roles
      : [];
  const entityRoleIdsFromNested = entityRolesRaw
    .map((r) => r?.entityRoleId ?? r?.Entity_Role_Id ?? r?.entity_role_id ?? r?.id)
    .filter((id) => id != null && id !== '');
  const entityRoleIdsRaw = Array.isArray(u.entityRoleIds)
    ? u.entityRoleIds
    : Array.isArray(u.entity_role_ids)
      ? u.entity_role_ids
      : entityRoleIdsFromNested;
  const entityRoleIds = [
    ...new Set(
      entityRoleIdsRaw
        .map((id) => Number(id) || id)
        .filter((id) => id != null && id !== '')
    ),
  ];

  const roleNamesFromEntity = entityRolesRaw
    .map((r) => r?.roleName ?? r?.Role_Name ?? r?.name)
    .filter(Boolean);
  const roles =
    roleNamesFromEntity.length > 0
      ? [...new Set(roleNamesFromEntity.map(String))]
      : normalizeRestaurantRoleNames(rawRoles);

  return {
    id: u.userId ?? u.user_id ?? u.id,
    firstName: u.firstName ?? u.first_name ?? '',
    lastName: u.lastName ?? u.last_name ?? '',
    email: u.email ?? u.Email ?? '',
    aadharNo: u.aadharNo ?? u.aadhar_no ?? u.aadharNumber ?? '',
    mobile: u.mobile ?? u.Mobile ?? '',
    roles,
    entityRoleIds,
    status: String(u.status ?? u.Status ?? '').toUpperCase(),
  };
}

/** Merge rows returned once per role into one user with combined roles. */
export function mergeRestaurantUserRows(rows) {
  const byId = new Map();
  for (const raw of rows) {
    const row = mapRestaurantUserRow(raw);
    if (!row?.id) continue;
    const key = String(row.id);
    if (byId.has(key)) {
      const existing = byId.get(key);
      existing.roles = [...new Set([...(existing.roles || []), ...(row.roles || [])])];
      existing.entityRoleIds = [
        ...new Set([...(existing.entityRoleIds || []), ...(row.entityRoleIds || [])]),
      ];
    } else {
      byId.set(key, row);
    }
  }
  return [...byId.values()];
}

/**
 * Soft-deactivate a restaurant user via PUT (same endpoint as update).
 * Avoids DELETE-with-body, which often fails in the browser with ERR_NETWORK / CORS.
 */
export async function deactivateRestaurantUser(
  targetUserId,
  { restaurantId, restaurantName, updatedById, userRow }
) {
  if (!userRow) {
    throw new Error('User not found');
  }

  const headers = { ...authHeaders(), 'Content-Type': 'application/json' };

  return axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantUserById(targetUserId),
    {
      firstName: userRow.firstName,
      lastName: userRow.lastName,
      mobile: userRow.mobile,
      email: userRow.email,
      aadharNo: userRow.aadharNo,
      restaurantId: String(restaurantId),
      restaurantName: restaurantName || '',
      entityRoleIds: userRow.entityRoleIds || [],
      roleNames: userRow.roles || [],
      status: 'INACTIVE',
      updatedById,
      fcmToken: null,
    },
    { headers }
  );
}

export async function updateRestaurantUser(userId, payload) {
  return axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantUserById(userId),
    payload,
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
}

export const extractRestaurantId = (item) =>
  item?.restaurantId ??
  item?.RestaurantId ??
  item?.restaurant_id ??
  item?.Restaurant_Id ??
  item?.id ??
  null;

export const extractRestaurantName = (item) =>
  item?.restaurantName ??
  item?.RestaurantName ??
  item?.restaurant_name ??
  item?.Restaurant_Name ??
  item?.name ??
  item?.Name ??
  '';

/** First defined value among keys on obj (supports camelCase, PascalCase, snake_case). */
export const pickValue = (obj, ...keys) => {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== undefined && value !== null) {
        return value;
      }
    }
  }
  return undefined;
};

export const normalizeRestaurantDetail = (data) => {
  if (data == null) return null;
  const raw = Array.isArray(data) ? data[0] : data;
  if (!raw || typeof raw !== 'object') return null;

  const isDefault = pickValue(raw, 'isDefault', 'IsDefault', 'is_default');

  const addresses = normalizeAddressesForTable({
    addresses: pickValue(raw, 'addresses', 'Addresses', 'Address') ?? [],
  });

  return {
    ...raw,
    restaurantId: pickValue(raw, 'restaurantId', 'RestaurantId', 'restaurant_id', 'Restaurant_Id'),
    restaurantName: extractRestaurantName(raw),
    restaurantShortCode:
      pickValue(raw, 'restaurantShortCode', 'RestaurantShortCode', 'restaurant_short_code') ?? '',
    contactNo: pickValue(raw, 'contactNo', 'ContactNo', 'contact_no'),
    email: pickValue(raw, 'email', 'Email') ?? '',
    cuisineType: pickValue(raw, 'cuisineType', 'CuisineType', 'cuisine_type') ?? '',
    fssaiNumber:
      pickValue(raw, 'fssaiNumber', 'FssaiNumber', 'fssai_number', 'Fssai_Number') ?? '',
    notes: pickValue(raw, 'notes', 'Notes') ?? '',
    status: pickValue(raw, 'status', 'Status') ?? '',
    isDefault,
    avgPrepTime: pickValue(raw, 'avgPrepTime', 'AvgPrepTime', 'avg_prep_time'),
    avgDeliveryTimePerKm: pickValue(
      raw,
      'avgDeliveryTimePerKm',
      'AvgDeliveryTimePerKm',
      'avg_delivery_time_per_km'
    ),
    addresses,
    image:
      pickValue(raw, 'image', 'Image', 'imagePath', 'Image_Path', 'image_path') ?? null,
    imageUrl:
      pickValue(raw, 'imageUrl', 'ImageUrl', 'image_url', 'Image_Url') ?? null,
  };
};

export const extractFoodCategoryId = (item) =>
  pickValue(item, 'foodCategoryId', 'FoodCategoryId', 'food_category_id', 'id') ?? null;

export const extractFoodCategoryName = (item) =>
  pickValue(
    item,
    'foodCategoryName',
    'FoodCategoryName',
    'food_category_name',
    'name',
    'Name'
  ) ?? '';

export const normalizeFoodCategories = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') return Object.values(data).flat();
  return [];
};

export const mapIngredientOption = (item) => {
  if (!item || typeof item !== 'object') return null;
  const ingredientId = pickValue(item, 'ingredientId', 'IngredientId', 'ingredient_id', 'id');
  const ingredientName = pickValue(
    item,
    'ingredientName',
    'IngredientName',
    'ingredient_name',
    'name',
    'Name'
  );
  if (ingredientId == null && !ingredientName) return null;
  const groceryCategoryId = pickValue(
    item,
    'groceryCategoryId',
    'Grocery_Category_Id',
    'grocery_category_id'
  );
  const categoryName = pickValue(item, 'categoryName', 'Category_Name', 'category_name');
  return {
    ingredientId,
    ingredientName: ingredientName ?? '',
    groceryCategoryId: groceryCategoryId ?? '',
    categoryName: categoryName ?? '',
    isAllergen: pickValue(item, 'isAllergen', 'IsAllergen', 'is_allergen'),
    isGluten: pickValue(item, 'isGluten', 'IsGluten', 'is_gluten'),
    isSugarFree: pickValue(item, 'isSugarFree', 'IsSugarFree', 'is_sugar_free'),
    linkId: pickValue(item, 'linkId', 'LinkId', 'link_id'),
  };
};

export const normalizeIngredientOptions = (data) => {
  const list = Array.isArray(data) ? data : data?.ingredients;
  if (!Array.isArray(list)) return [];
  return list.map(mapIngredientOption).filter(Boolean);
};

export const normalizeAddressesForTable = (restaurant) => {
  const raw = restaurant?.addresses ?? restaurant?.Addresses ?? restaurant?.Address ?? [];
  const arr = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? [raw] : [];
  return arr.map((addr) => {
    const homeDelivery = pickValue(
      addr,
      'homeDeliveryAvailable',
      'HomeDeliveryAvailable',
      'home_delivery_available'
    );
    return {
    addressId: pickValue(addr, 'addressId', 'AddressId', 'address_id') ?? null,
    addressLine1:
      pickValue(addr, 'addressLine1', 'AddressLine1', 'address_line1') ?? '',
    addressLine2:
      pickValue(addr, 'addressLine2', 'AddressLine2', 'address_line2') ?? '',
    areaName: pickValue(addr, 'areaName', 'AreaName', 'area_name') ?? '',
    city: pickValue(addr, 'city', 'City') ?? '',
    state: pickValue(addr, 'state', 'State') ?? '',
    country: pickValue(addr, 'country', 'Country') ?? '',
    postalCode: pickValue(addr, 'postalCode', 'PostalCode', 'postal_code') ?? '',
    homeDeliveryAvailable:
      homeDelivery === true ||
      homeDelivery === 'true' ||
      homeDelivery === 1 ||
      homeDelivery === '1',
    lat:
      addr.lat != null && String(addr.lat).trim() !== ''
        ? String(addr.lat)
        : addr.latitude != null
          ? String(addr.latitude)
          : '',
    lon:
      addr.lon != null && String(addr.lon).trim() !== ''
        ? String(addr.lon)
        : addr.longitude != null
          ? String(addr.longitude)
          : '',
    longitude: parseFloat(addr.lon ?? addr.Longitude ?? addr.longitude ?? 0) || 0,
    latitude: parseFloat(addr.lat ?? addr.Latitude ?? addr.latitude ?? 0) || 0,
  };
  });
};

/** Payload for PUT /restaurants/addresses/{addressId} */
export const buildAddressUpdatePayload = (address, updatedById) => ({
  addressLine1: address.addressLine1 ?? '',
  addressLine2: address.addressLine2 ?? '',
  areaName: address.areaName ?? '',
  postalCode: address.postalCode ?? '',
  city: address.city ?? '',
  state: address.state ?? '',
  country: address.country ?? '',
  lat: Number(address.lat ?? address.latitude),
  lon: Number(address.lon ?? address.longitude),
  homeDeliveryAvailable:
    address.homeDeliveryAvailable === true || address.homeDeliveryAvailable === 'true',
  updatedById: updatedById ?? 1,
});

export const updateRestaurantAddress = async (addressId, address, updatedById) => {
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantAddressById(addressId),
    buildAddressUpdatePayload(address, updatedById),
    { headers: authHeaders() }
  );
  return response.data;
};

/** GET /address/suggest?address=...&types=... */
export const fetchAddressSuggestions = async (query, options = {}) => {
  const params = { address: query };
  if (options.types) params.types = options.types;
  const response = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.addressSuggest, {
    params,
    headers: authHeaders(),
  });
  const list = response.data?.suggestions;
  return Array.isArray(list) ? list : [];
};

/** GET /address/suggest/details?placeId=... */
export const fetchAddressPlaceDetails = async (placeId) => {
  const response = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.addressSuggestDetails, {
    params: { placeId },
    headers: authHeaders(),
  });
  return response.data;
};

const parseIsDefaultValue = (value) => {
  if (value === true || value === 'true' || value === '1' || value === 1) return 1;
  if (value === false || value === 'false' || value === '0' || value === 0) return 0;
  return 0;
};

const parseContactNo = (contactNo) => {
  if (contactNo === '' || contactNo == null) return contactNo;
  const digits = String(contactNo).replace(/\D/g, '');
  if (digits) {
    const num = Number(digits);
    return Number.isFinite(num) ? num : contactNo;
  }
  return contactNo;
};

/** Payload for PUT /restaurants/{restaurantId} */
export const buildRestaurantUpdatePayload = (form, updatedById) => {
  const uid = updatedById ?? 1;
  const shortCode = normalizeRestaurantShortCode(form.restaurantShortCode);

  const payload = {
    restaurantName: form.restaurantName ?? '',
    restaurantShortCode: shortCode,
    contactNo: parseContactNo(form.contactNo),
    email: form.email ?? '',
    updatedById: uid,
    cuisineType: form.cuisineType ?? '',
    notes: form.notes ?? '',
    isDefault: parseIsDefaultValue(form.isDefault),
    ...(parseIsDefaultValue(form.isDefault) === 0 &&
    form.newDefaultRestaurantId != null &&
    String(form.newDefaultRestaurantId).trim() !== ''
      ? { newDefaultRestaurantId: parseInt(form.newDefaultRestaurantId, 10) }
      : {}),
    ...(form.status != null && String(form.status).trim() !== ''
      ? { status: String(form.status).trim().toUpperCase() }
      : {}),
  };

  const imageUrl = form.imageUrl ?? form.image ?? null;
  if (imageUrl) {
    payload.image = imageUrl;
  }

  if (form.avgPrepTime != null && form.avgPrepTime !== '') {
    payload.avgPrepTime = parseInt(form.avgPrepTime, 10);
  }
  if (form.avgDeliveryTimePerKm != null && form.avgDeliveryTimePerKm !== '') {
    payload.avgDeliveryTimePerKm = parseInt(form.avgDeliveryTimePerKm, 10);
  }

  const primaryAddress = form.primaryAddress;
  if (primaryAddress) {
    payload.address = {
      ...buildAddressUpdatePayload(primaryAddress, uid),
    };
  }

  return payload;
};

export const updateRestaurant = async (restaurantId, form, updatedById) => {
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantById(restaurantId),
    buildRestaurantUpdatePayload(form, updatedById),
    { headers: authHeaders() }
  );
  return response.data;
};

export const extractRestaurantAddresses = (restaurant) => {
  const raw = restaurant?.addresses ?? restaurant?.Addresses ?? restaurant?.Address ?? [];
  const arr = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? [raw] : [];
  return arr.map((addr) => ({
    line1: addr.addressLine1 ?? addr.AddressLine1 ?? '',
    line2: addr.addressLine2 ?? addr.AddressLine2 ?? '',
    city: addr.city ?? addr.City ?? '',
    state: addr.state ?? addr.State ?? '',
    country: addr.country ?? addr.Country ?? '',
    postalCode: addr.postalCode ?? addr.PostalCode ?? '',
    homeDelivery:
      addr.homeDeliveryAvailable ?? addr.HomeDeliveryAvailable ?? false,
  }));
};

export const fetchRestaurantById = async (restaurantId) => {
  const response = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.restaurantById(restaurantId), {
    headers: authHeaders(),
  });
  return normalizeRestaurantDetail(response.data);
};

export const fetchRestaurantRootAdmin = async (restaurantId) => {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantRootAdminByRestaurantId(restaurantId),
    { headers: authHeaders() }
  );
  const owner = response.data?.owner;
  if (!owner || typeof owner !== 'object') return null;
  return mapRestaurantUserRow(owner);
};

/**
 * Loads all restaurants for a user: resolves IDs from user-restaurant list endpoints,
 * then fetches full details from GET /restaurants/{id}.
 */
export const fetchUserRestaurants = async (userId) => {
  if (!userId) return [];

  const headers = authHeaders();
  const list = [];
  const seenIds = new Set();

  const appendUnique = (data) => {
    const rows = Array.isArray(data) ? data : [];
    for (const row of rows) {
      const id = extractRestaurantId(row);
      if (!id) continue;
      const key = String(id);
      const existingIndex = list.findIndex(
        (item) => String(extractRestaurantId(item)) === key
      );
      if (existingIndex === -1) {
        seenIds.add(key);
        list.push(row);
        continue;
      }
      if (restaurantSummaryScore(row) > restaurantSummaryScore(list[existingIndex])) {
        list[existingIndex] = row;
      }
    }
  };

  for (const url of [
    RESTAURANT_API_PATHS.userRestaurants(userId),
    RESTAURANT_API_PATHS.userRestaurantIds(userId),
  ]) {
    try {
      const res = await axiosRestaurantInstance.get(url, { headers });
      appendUnique(res.data);
    } catch (error) {
      console.warn(
        `Could not load restaurants from ${url}:`,
        error?.response?.data?.message || error?.message
      );
    }
  }

  let ids = [...seenIds];

  if (ids.length === 0) {
    const storedId = localStorage.getItem('restaurantId');
    let userRestaurantId = null;
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      userRestaurantId = user?.restaurantId ?? null;
    } catch {
      // ignore
    }
    const fallbackId = storedId || userRestaurantId;
    if (fallbackId) ids = [fallbackId];
  }

  const restaurants = await Promise.all(
    ids.map(async (id) => {
      const summary = list.find((r) => String(extractRestaurantId(r)) === String(id));
      if (summary) {
        const normalized = normalizeRestaurantDetail(summary);
        if (normalized?.restaurantName && hasRestaurantStatus(normalized)) {
          return normalized;
        }
      }
      try {
        return await fetchRestaurantById(id);
      } catch (error) {
        console.error(`Error fetching restaurant ${id}:`, error);
        if (!summary) return null;
        return normalizeRestaurantDetail(summary) || {
          restaurantId: extractRestaurantId(summary),
          restaurantName: extractRestaurantName(summary),
        };
      }
    })
  );

  return restaurants
    .filter(Boolean)
    .sort((a, b) => extractRestaurantName(a).localeCompare(extractRestaurantName(b)));
};

export async function fetchRestaurantServices(restaurantId) {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantServices(restaurantId),
    { headers: authHeaders() }
  );
  return response.data?.services ?? [];
}

export async function addRestaurantService(restaurantId, payload) {
  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.restaurantServices(restaurantId),
    payload,
    { headers: authHeaders() }
  );
  return response.data;
}

export async function updateRestaurantService(serviceId, payload) {
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantServiceById(serviceId),
    payload,
    { headers: authHeaders() }
  );
  return response.data;
}

export async function fetchRestaurantMealDeliveryTimes(restaurantId) {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantJreatsMealDeliveryTimes(restaurantId),
    { headers: authHeaders() }
  );
  return response.data?.mealDeliveryTimes ?? response.data?.meal_delivery_times ?? {};
}

export async function updateRestaurantMealDeliveryTimes(restaurantId, mealDeliveryTimes, userId) {
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantJreatsMealDeliveryTimes(restaurantId),
    {
      mealDeliveryTimes,
      updatedById: userId,
    },
    { headers: authHeaders() }
  );
  return response.data;
}

export async function fetchRestaurantMyeatsOrgMealDeliveryTimes(restaurantId, organizationId) {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantMyeatsOrgMealDeliveryTimes(restaurantId),
    {
      headers: authHeaders(),
      params: { organizationId },
    }
  );
  return response.data;
}

export async function updateRestaurantMyeatsOrgMealDeliveryTimes(
  restaurantId,
  organizationId,
  mealDeliveryTimes,
  userId,
) {
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantMyeatsOrgMealDeliveryTimes(restaurantId),
    {
      organizationId,
      mealDeliveryTimes,
      updatedById: userId,
    },
    { headers: authHeaders() }
  );
  return response.data;
}

export async function fetchRestaurantMyeatsDeliverySlots(restaurantId) {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantMyeatsDeliverySlots(restaurantId),
    { headers: authHeaders() }
  );
  return response.data ?? {};
}

export async function updateRestaurantMyeatsDeliverySlots(restaurantId, payload, userId) {
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantMyeatsDeliverySlots(restaurantId),
    {
      ...payload,
      updatedById: userId,
    },
    { headers: authHeaders() }
  );
  return response.data;
}

export async function fetchRestaurantMyeatsOffices(restaurantId) {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantMyeatsOffices(restaurantId),
    { headers: authHeaders() },
  );
  return Array.isArray(response.data?.offices) ? response.data.offices : [];
}

export async function searchRestaurantMyeatsOffices(restaurantId, { name = '', radiusKm = 10 } = {}) {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantMyeatsOfficesSearch(restaurantId),
    {
      headers: authHeaders(),
      params: {
        ...(name ? { name } : {}),
        radiusKm,
      },
    },
  );
  return Array.isArray(response.data?.offices) ? response.data.offices : [];
}

export async function mapRestaurantMyeatsOffice(restaurantId, organizationId, userId) {
  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.restaurantMyeatsOffices(restaurantId),
    {
      organizationId,
      updatedById: userId,
    },
    { headers: authHeaders() },
  );
  return response.data;
}

export async function unmapRestaurantMyeatsOffice(restaurantId, organizationId) {
  const response = await axiosRestaurantInstance.delete(
    RESTAURANT_API_PATHS.restaurantMyeatsOfficeByOrg(restaurantId, organizationId),
    { headers: authHeaders() },
  );
  return response.data;
}

export async function fetchRestaurantCateringFarmhouses(restaurantId) {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantCateringFarmhouses(restaurantId),
    { headers: authHeaders() },
  );
  return response.data || { farmhouses: [], deliverySlots: null };
}

export async function searchRestaurantCateringFarmhouses(
  restaurantId,
  { name = '', radiusKm = 10, hallType = 'FARMHOUSE' } = {},
) {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantCateringFarmhousesSearch(restaurantId),
    {
      headers: authHeaders(),
      params: {
        ...(name ? { name } : {}),
        radiusKm,
        hallType,
      },
    },
  );
  return Array.isArray(response.data?.farmhouses) ? response.data.farmhouses : [];
}

export async function mapRestaurantCateringFarmhouse(restaurantId, partyHallId, userId) {
  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.restaurantCateringFarmhouses(restaurantId),
    {
      partyHallId,
      updatedById: userId,
    },
    { headers: authHeaders() },
  );
  return response.data;
}

export async function unmapRestaurantCateringFarmhouse(restaurantId, partyHallId) {
  const response = await axiosRestaurantInstance.delete(
    RESTAURANT_API_PATHS.restaurantCateringFarmhouseById(restaurantId, partyHallId),
    { headers: authHeaders() },
  );
  return response.data;
}

export async function fetchRestaurantJreatsSchools(restaurantId) {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantJreatsSchools(restaurantId),
    { headers: authHeaders() },
  );
  return Array.isArray(response.data?.schools) ? response.data.schools : [];
}

export async function searchRestaurantJreatsSchools(restaurantId, { name = '', radiusKm = 10 } = {}) {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantJreatsSchoolsSearch(restaurantId),
    {
      headers: authHeaders(),
      params: {
        ...(name ? { name } : {}),
        radiusKm,
      },
    },
  );
  return Array.isArray(response.data?.schools) ? response.data.schools : [];
}

export async function mapRestaurantJreatsSchool(restaurantId, organizationId, userId) {
  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.restaurantJreatsSchools(restaurantId),
    {
      organizationId,
      updatedById: userId,
    },
    { headers: authHeaders() },
  );
  return response.data;
}

export async function unmapRestaurantJreatsSchool(restaurantId, organizationId) {
  const response = await axiosRestaurantInstance.delete(
    RESTAURANT_API_PATHS.restaurantJreatsSchoolByOrg(restaurantId, organizationId),
    { headers: authHeaders() },
  );
  return response.data;
}

/** Property key for POS UPI QR (restaurant_property_t). */
export const POS_UPI_VPA_PROPERTY_KEY = 'POS_UPI_VPA';

/** GET /restaurant/:id/properties */
export async function fetchRestaurantProperties(restaurantId) {
  if (restaurantId == null || restaurantId === '') return null;
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantProperties(restaurantId),
    { headers: authHeaders() },
  );
  return response.data ?? null;
}

/** Load POS UPI VPA from restaurant properties (empty string if unset). */
export async function fetchRestaurantPosUpiVpa(restaurantId) {
  const data = await fetchRestaurantProperties(restaurantId);
  if (!data) return '';
  const fromTop = data.posUpiVpa ?? data.pos_upi_vpa;
  if (fromTop != null && String(fromTop).trim()) return String(fromTop).trim();
  const props = data.properties || data.Properties || {};
  const v = props[POS_UPI_VPA_PROPERTY_KEY] ?? props.posUpiVpa;
  return v != null ? String(v).trim() : '';
}

/**
 * PUT /restaurant/:id/properties — upsert POS_UPI_VPA (and optional extra map).
 */
export async function saveRestaurantPosUpiVpa(restaurantId, posUpiVpa, userId) {
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantProperties(restaurantId),
    {
      posUpiVpa: String(posUpiVpa || '').trim(),
      updatedById: userId != null ? Number(userId) : undefined,
    },
    { headers: authHeaders() },
  );
  const data = response.data ?? {};
  return String(data.posUpiVpa ?? data.pos_upi_vpa ?? posUpiVpa ?? '').trim();
}

export async function fetchJreatsPlatformFee() {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.jreatsPlatformFee,
    { headers: authHeaders() }
  );
  return response.data?.platformFee ?? 10;
}

export async function updateJreatsPlatformFee(platformFee, userId) {
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.jreatsPlatformFee,
    {
      platformFee,
      updatedById: userId,
    },
    { headers: authHeaders() }
  );
  return response.data;
}

